import { supabase } from './supabase';

const API_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3001';

export interface UploadResult {
  success: boolean;
  fileUrl?: string;
  fileName?: string;
  error?: string;
}

const getAuthToken = (): string | null => {
  return localStorage.getItem('@sistema-up:token');
};

const getFrontendSecret = (): string => {
  // Para produção, usar o valor hardcoded (mesmo do backend)
  // Em desenvolvimento, usar variável de ambiente
  if (process.env.NODE_ENV === 'production') {
    return 'a13a589f58f942325ba655f2be167a982bb6fa052854dc3c627f442b6cf4b0cb';
  }
  return process.env.REACT_APP_FRONTEND_SECRET || '';
};

export class UploadService {
  static async uploadFile(
    file: File,
    bucket: string = 'documentos',
    folder: string = 'geral'
  ): Promise<UploadResult> {
    try {
      if (!file) {
        return { success: false, error: 'Nenhum arquivo selecionado' };
      }

      const token = getAuthToken();
      if (!token) {
        return { success: false, error: 'Usuário não autenticado' };
      }

      const frontendSecret = getFrontendSecret();

      const formData = new FormData();
      formData.append('file', file);
      formData.append('bucket', bucket);
      formData.append('folder', folder);

      const response = await fetch(`${API_URL}/api/storage/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Frontend-Secret': frontendSecret
        },
        body: formData
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        console.error('Erro no upload via API:', result);
        return {
          success: false,
          error: result.error || `Erro ao fazer upload (${response.status})`
        };
      }

      return {
        success: true,
        fileUrl: result.data.publicUrl,
        fileName: result.data.path
      };

    } catch (error) {
      console.error('Erro inesperado no upload:', error);
      return {
        success: false,
        error: 'Erro inesperado ao fazer upload do arquivo'
      };
    }
  }

  static async uploadPedidoFile(
    file: File,
    pedidoId: string
  ): Promise<UploadResult> {
    try {
      if (!file) {
        return { success: false, error: 'Nenhum arquivo selecionado' };
      }

      const token = getAuthToken();
      if (!token) {
        return { success: false, error: 'Usuário não autenticado' };
      }

      const frontendSecret = getFrontendSecret();

      const formData = new FormData();
      formData.append('file', file);
      formData.append('bucket', 'pedidos');
      formData.append('folder', `pedido-${pedidoId}`);
      
      const response = await fetch(`${API_URL}/api/storage/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Frontend-Secret': frontendSecret
        },
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erro na resposta:', errorText);
        return { 
          success: false, 
          error: `Erro ${response.status}: ${errorText}` 
        };
      }

      const result = await response.json();

      return {
        success: true,
        fileUrl: result.data?.publicUrl || result.data?.url,
        fileName: result.data?.path || result.data?.fileName
      };
    } catch (error) {
      console.error('Erro no upload de pedido:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido'
      };
    }
  }

  static async uploadSolicitacaoFile(
    file: File,
    solicitacaoId: string
  ): Promise<UploadResult> {
    return this.uploadFile(file, 'documentos', `solicitacao-${solicitacaoId}`);
  }

  static async deleteFile(
    filePath: string,
    bucket: string = 'documentos'
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const token = getAuthToken();
      if (!token) {
        return { success: false, error: 'Usuário não autenticado' };
      }

      const frontendSecret = getFrontendSecret();
      const response = await fetch(`${API_URL}/api/storage/delete`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Frontend-Secret': frontendSecret
        },
        body: JSON.stringify({ bucket, path: filePath })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        console.error('Erro ao deletar arquivo via API:', result);
        return {
          success: false,
          error: result.error || 'Erro ao deletar arquivo'
        };
      }

      return { success: true };
    } catch (error) {
      console.error('Erro inesperado ao deletar:', error);
      return {
        success: false,
        error: 'Erro inesperado ao deletar arquivo'
      };
    }
  }

  static getFileIcon(fileName: string): string {
    if (!fileName) return '📎';
    
    const ext = fileName.split('.').pop()?.toLowerCase();
    
    switch (ext) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'xls':
      case 'xlsx':
        return '📊';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return '🖼️';
      default:
        return '📎';
    }
  }

  static formatFileSize(bytes: number): string {
    if (!bytes || bytes === 0 || isNaN(bytes) || !isFinite(bytes)) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  static validateFileType(file: File): { valid: boolean; error?: string } {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/jpeg',
      'image/png',
      'image/gif'
    ];

    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Tipo de arquivo não permitido. Use PDF, DOC, DOCX, XLS, XLSX, JPG, PNG ou GIF'
      };
    }

    return { valid: true };
  }

  static validateFileSize(file: File, maxSizeMB: number = 10): { valid: boolean; error?: string } {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    
    if (file.size > maxSizeBytes) {
      return {
        valid: false,
        error: `Arquivo muito grande. Tamanho máximo permitido: ${maxSizeMB}MB`
      };
    }

    return { valid: true };
  }

  static getPublicUrl(storagePath: string, bucket: string = 'documentos'): string {
    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(storagePath);
    
    return publicUrl;
  }
}

// Adicionar ao window para testes
if (typeof window !== 'undefined') {
  (window as any).UploadService = UploadService;
}
